import { useState } from 'react';
import { Star, ThumbsUp, MessageSquare, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { format } from 'date-fns';

// Star rating display component
export function StarRating({ rating, size = 'sm' }) {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${
            star <= rating 
              ? 'fill-amber-400 text-amber-400' 
              : 'fill-slate-200 text-slate-200'
          }`}
        />
      ))}
    </div>
  );
}

// Star rating input component
export function StarRatingInput({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="focus:outline-none"
        >
          <Star
            className={`w-8 h-8 transition-colors ${
              star <= (hovered || value)
                ? 'fill-amber-400 text-amber-400'
                : 'fill-slate-200 text-slate-200 hover:fill-amber-200 hover:text-amber-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

// Review summary stats component
export function ReviewStats({ stats }) {
  if (!stats || stats.total_count === 0) {
    return (
      <div className="text-slate-500 text-sm">
        No reviews yet
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <StarRating rating={Math.round(stats.average_rating)} />
        <span className="font-semibold text-slate-900">
          {stats.average_rating.toFixed(1)}
        </span>
      </div>
      <span className="text-slate-500">
        {stats.total_count} {stats.total_count === 1 ? 'review' : 'reviews'}
      </span>
      {stats.recommend_percentage > 0 && (
        <span className="text-green-600 flex items-center gap-1">
          <ThumbsUp className="w-4 h-4" />
          {stats.recommend_percentage}% recommend
        </span>
      )}
    </div>
  );
}

// Single review card component
export function ReviewCard({ 
  review, 
  showProperty = false,
  showReplyForm = false,
  onReply,
  isAdmin = false,
  onToggleVisibility
}) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmitReply = async () => {
    if (!replyText.trim() || !onReply) return;
    setSubmitting(true);
    try {
      await onReply(review.id, replyText);
      setShowReply(false);
      setReplyText('');
    } catch (error) {
      console.error('Reply failed:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className={`p-4 rounded-lg border ${
        review.is_hidden ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'
      }`}
      data-testid="review-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-semibold">
                {review.guest_name?.charAt(0)?.toUpperCase() || 'G'}
              </span>
            </div>
            <div>
              <p className="font-medium text-slate-900">{review.guest_name}</p>
              <p className="text-xs text-slate-500">
                {format(new Date(review.created_at), 'MMMM d, yyyy')}
              </p>
            </div>
          </div>
          
          {/* Property name if shown */}
          {showProperty && review.property_title && (
            <p className="text-sm text-slate-600 mb-2">
              Stayed at <span className="font-medium">{review.property_title}</span>
            </p>
          )}
          
          {/* Rating */}
          <div className="flex items-center gap-2 mb-2">
            <StarRating rating={review.rating} />
            {review.would_recommend && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <ThumbsUp className="w-3 h-3" />
                Would recommend
              </span>
            )}
          </div>
          
          {/* Title */}
          {review.title && (
            <h4 className="font-medium text-slate-900 mb-1">{review.title}</h4>
          )}
          
          {/* Comment */}
          <p className="text-slate-600 text-sm leading-relaxed">{review.comment}</p>
          
          {/* Host Reply */}
          {review.host_reply && (
            <div className="mt-4 pl-4 border-l-2 border-primary/30">
              <p className="text-xs text-slate-500 mb-1">
                Host response • {format(new Date(review.host_reply_at), 'MMM d, yyyy')}
              </p>
              <p className="text-sm text-slate-700">{review.host_reply}</p>
            </div>
          )}
          
          {/* Reply Form */}
          {showReplyForm && !review.host_reply && (
            <div className="mt-4">
              {showReply ? (
                <div className="space-y-2">
                  <Textarea
                    placeholder="Write your response..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={3}
                    maxLength={1000}
                  />
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={handleSubmitReply}
                      disabled={!replyText.trim() || submitting}
                    >
                      {submitting ? 'Sending...' : 'Send Reply'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setShowReply(false);
                        setReplyText('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowReply(true)}
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Reply
                </Button>
              )}
            </div>
          )}
        </div>
        
        {/* Admin actions */}
        {isAdmin && onToggleVisibility && (
          <Button
            size="sm"
            variant={review.is_hidden ? 'default' : 'outline'}
            onClick={() => onToggleVisibility(review.id)}
            className={review.is_hidden ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {review.is_hidden ? (
              <>
                <Eye className="w-4 h-4 mr-1" />
                Show
              </>
            ) : (
              <>
                <EyeOff className="w-4 h-4 mr-1" />
                Hide
              </>
            )}
          </Button>
        )}
      </div>
      
      {/* Hidden badge */}
      {review.is_hidden && (
        <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
          <EyeOff className="w-3 h-3" />
          This review is hidden from public view
        </div>
      )}
    </div>
  );
}

export default ReviewCard;
